<script setup lang="ts" generic="Row extends object">
  import TableColumnSearch from '@/components/table/TableColumnSearch.vue'
  import TablePagination from '@/components/table/TablePagination.vue'
  import { useXPathAction, type Column } from '@logistiek-zonder-papier/lzp-library'
  import { useTranslation } from 'i18next-vue'
  import { computed, ref, watch, type StyleValue } from 'vue'
  import { useRoute } from 'vue-router'

  const {
    pageSize = 10,
    rows,
    disablePagination,
    rowKey,
    columns,
  } = defineProps<{
    rows: Row[]
    columns: Column[]
    disablePagination?: boolean
    pageSize?: number
    doNotFill?: boolean
    rowKey?: keyof Row
  }>()

  const emit = defineEmits<{
    rowClick: [row: Row]
  }>()

  defineSlots<
    {
      [key: `cell-${string}`]: (props: { row: Row; value: unknown }) => unknown
    } & {
      empty?: () => unknown
    }
  >()

  const { t: translate } = useTranslation()
  const xPathActions = useXPathAction()
  const route = useRoute()

  const columnTerms = ref<Record<string, string>>({})
  const page = ref(1)
  const currentPageSize = ref(readStoredPageSize() ?? pageSize)

  watch(
    () => pageSize,
    pageSize => {
      currentPageSize.value = pageSize
    }
  )

  function getPageSizeStorageKey() {
    return `tablePageSize:${route.matched.at(-1)?.path ?? route.path}`
  }

  function readStoredPageSize() {
    const storedPageSize = Number(localStorage.getItem(getPageSizeStorageKey()))
    return Number.isInteger(storedPageSize) && storedPageSize > 0 ? storedPageSize : null
  }

  function setPageSize(pageSize: number) {
    currentPageSize.value = pageSize
    localStorage.setItem(getPageSizeStorageKey(), String(pageSize))
  }

  function getCellValue(row: Row, column: Column) {
    return xPathActions.resolvePath(column.key, row as Record<string, unknown>)
  }

  const filteredRows = computed(() =>
    rows.filter(row =>
      Object.entries(columnTerms.value).every(([key, term]) => {
        if (term.length === 0 || !columns.some(column => column.key === key)) return true
        const value = xPathActions.resolvePath(key, row as Record<string, unknown>)
        return String(value ?? '')
          .toLowerCase()
          .includes(term.toLowerCase())
      })
    )
  )

  const totalPages = computed(() =>
    Math.max(1, Math.ceil(filteredRows.value.length / currentPageSize.value))
  )

  const visibleRows = computed(() => {
    if (disablePagination) return filteredRows.value

    const start = (page.value - 1) * currentPageSize.value
    return filteredRows.value.slice(start, start + currentPageSize.value)
  })

  watch([filteredRows, currentPageSize], () => {
    page.value = 1
  })

  function getRowKey(row: Row, index: number) {
    return rowKey ? String(row[rowKey]) : index
  }

  const columnStyles = computed(() => {
    const styles = new Map<string, Record<string, string>>()

    for (const column of columns) {
      styles.set(
        column.key,
        column.width
          ? {
              width: column.width,
              minWidth: column.width,
              maxWidth: column.width,
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
            }
          : {}
      )
    }

    const left: string[] = []
    for (const column of columns) {
      if (column.sticky !== 'left') continue
      const style = styles.get(column.key)!
      style['position'] = 'sticky'
      style['left'] = left.length ? `calc(${left.join(' + ')})` : '0px'
      left.push(column.width ?? '0px')
    }

    const right: string[] = []
    for (const column of [...columns].reverse()) {
      if (column.sticky !== 'right') continue
      const style = styles.get(column.key)!
      style['position'] = 'sticky'
      style['right'] = right.length ? `calc(${right.join(' + ')})` : '0px'
      right.push(column.width ?? '0px')
    }

    return styles
  })

  const gridTemplateColumns = computed(() =>
    columns
      .map(column => {
        if (column.width) return column.width
        if (column.sticky === 'right') return 'max-content'
        return 'minmax(max-content, 1fr)'
      })
      .join(' ')
  )

  function getColumnStyle(column: Column): StyleValue {
    return columnStyles.value.get(column.key)
  }
</script>

<template>
  <div class="table-card" :class="{ 'does-not-fill': doNotFill }">
    <div class="table-scroll">
      <table class="table" :style="{ gridTemplateColumns }">
        <thead>
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              class="head-cell"
              :class="column.sticky && `is-sticky-${column.sticky}`"
              :style="getColumnStyle(column)"
            >
              <div class="head-label">{{ translate(column.label ?? column.key) }}</div>
              <TableColumnSearch v-if="column.searchable" v-model="columnTerms[column.key]" />
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="(row, rowIndex) in visibleRows"
            :key="getRowKey(row, rowIndex)"
            class="body-row"
            @click="emit('rowClick', row)"
          >
            <td
              v-for="column in columns"
              :key="column.key"
              class="body-cell"
              :class="column.sticky && `is-sticky-${column.sticky}`"
              :style="getColumnStyle(column)"
            >
              <slot :name="`cell-${column.key}`" :row="row" :value="getCellValue(row, column)">
                {{ getCellValue(row, column) }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="visibleRows.length === 0" class="empty">
        <slot name="empty">{{ translate('table.noResults') }}</slot>
      </div>
    </div>

    <TablePagination
      v-if="!disablePagination"
      v-model="page"
      :page-size="currentPageSize"
      @update:page-size="setPageSize"
      :total-pages="totalPages"
    />
  </div>
</template>

<style scoped>
  .table-card {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--colours-grey-1);
    border: 1px solid var(--colours-grey-3);
    border-radius: 1rem;
    box-shadow: 0 1rem 2rem 0 var(--colours-shadow-1);
  }

  .table-card.does-not-fill {
    flex: 0 0 auto;
  }

  .table-scroll {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: auto;
  }

  .does-not-fill .table-scroll {
    flex: 0 0 auto;
  }

  .table {
    flex: 0 0 auto;
    display: grid;
    width: fit-content;
    min-width: 100%;
  }

  .table thead,
  .table tbody,
  .table tr {
    display: contents;
  }

  .head-cell {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: var(--spacing-s) var(--spacing-s) var(--spacing-xs);
    text-align: left;
    white-space: nowrap;
    background: var(--colours-grey-1);
    box-shadow: inset 0 -1px 0 0 var(--colours-grey-3);
  }

  .head-label {
    font-size: 0.875rem;
    font-weight: 700;
    user-select: none;
  }

  .head-cell:has(.column-search) .head-label {
    margin-bottom: var(--spacing-xs);
  }

  .body-cell {
    padding: 0.5rem var(--spacing-s);
    font-size: 0.875rem;
    white-space: nowrap;
    align-content: center;
    background: var(--colours-grey-1);
    border-bottom: 1px solid var(--colours-grey-3);
  }

  .body-row:hover .body-cell {
    background: var(--colours-grey-2);
  }

  .is-sticky-left,
  .is-sticky-right {
    z-index: 1;
  }

  .head-cell.is-sticky-left,
  .head-cell.is-sticky-right {
    z-index: 3;
  }

  .is-sticky-left {
    box-shadow: 1px 0 0 0 var(--colours-grey-3);
  }

  .head-cell.is-sticky-left {
    box-shadow:
      1px 0 0 0 var(--colours-grey-3),
      inset 0 -1px 0 0 var(--colours-grey-3);
  }

  .empty {
    flex: 1 0 auto;
    position: sticky;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-m);
    color: var(--colours-grey-4);
    font-size: 0.875rem;
  }
</style>
